import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { API_URL } from '../../utils';
import { commonDialogStyles } from '../common/dialogStyles';

interface AnalysisDialogProps {
  open: boolean;
  onClose: () => void;
  onAnalyze: (startDate: string, endDate: string, goal: string) => void;
  selectedDate: string;
  isLoading: boolean;
  onServiceUnavailable?: () => void;
}

interface GoalValidation {
  isValid: boolean;
  feedback: string;
}

export const AnalysisDialog: React.FC<AnalysisDialogProps> = ({
  open,
  onClose,
  onAnalyze,
  selectedDate,
  isLoading,
  onServiceUnavailable
}) => {
  // Date range mode: 'preset' or 'custom'
  const [dateRangeMode, setDateRangeMode] = useState<'preset' | 'custom'>('preset');
  const [presetRange, setPresetRange] = useState<string>('last7Days');
  
  // Initialize dates based on selectedDate, fallback to today if no date selected
  const initialDate = selectedDate ? new Date(selectedDate) : new Date();
  const [startDate, setStartDate] = useState<Date | null>(initialDate);
  const [endDate, setEndDate] = useState<Date | null>(initialDate);
  
  // Goal selection
  const [goalType, setGoalType] = useState<string>('muscleGroups');
  const [customGoal, setCustomGoal] = useState('');
  const [goalValidation, setGoalValidation] = useState<GoalValidation | null>(null);
  const [isValidatingGoal, setIsValidatingGoal] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  
  const { t, language } = useLanguage();
  const { fetchWithAuth } = useAuth();

  // Helper function to get the most recent Monday (or today if today is Monday)
  const getMostRecentMonday = useCallback((date: Date): Date => {
    const result = new Date(date);
    const day = result.getDay();
    const diff = day === 0 ? 6 : day - 1; // If Sunday (0), go back 6 days; otherwise go back to Monday
    result.setDate(result.getDate() - diff);
    return result;
  }, []);

  // Helper function to get the first day of the current month
  const getFirstDayOfMonth = useCallback((date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, []);

  // Calculate dates based on preset selection
  const calculatePresetDates = useCallback((preset: string): { start: Date; end: Date } => {
    const today = new Date();
    const end = today;
    let start = today;

    switch (preset) {
      case 'thisWeek':
        start = getMostRecentMonday(today);
        break;
      case 'last7Days':
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        break;
      case 'thisMonth':
        start = getFirstDayOfMonth(today);
        break;
      case 'last30Days':
        start = new Date(today);
        start.setDate(start.getDate() - 29);
        break;
      default:
        start = new Date(today);
        start.setDate(start.getDate() - 6);
    }

    return { start, end };
  }, [getMostRecentMonday, getFirstDayOfMonth]);

  // Update dates when preset changes
  useEffect(() => {
    if (dateRangeMode === 'preset') {
      const { start, end } = calculatePresetDates(presetRange);
      setStartDate(start);
      setEndDate(end);
    }
  }, [presetRange, dateRangeMode, calculatePresetDates]);

  // Reset all state when dialog opens
  useEffect(() => {
    if (open) {
      setDateRangeMode('preset');
      setPresetRange('last7Days');
      setGoalType('muscleGroups');
      setCustomGoal('');
      setGoalValidation(null);
      setValidationError(null);
      
      // Set initial dates for last 7 days
      const { start, end } = calculatePresetDates('last7Days');
      setStartDate(start);
      setEndDate(end);
    }
  }, [open, calculatePresetDates]);

  const validateGoal = async () => {
    if (!customGoal.trim()) {
      setGoalValidation({ isValid: false, feedback: 'Please enter a goal before validation.' });
      return;
    }

    setIsValidatingGoal(true);
    setValidationError(null);
    setGoalValidation(null);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/workouts/validate-goal?goal=${encodeURIComponent(customGoal)}&language=${language}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to validate goal');
      }
      
      const validation: GoalValidation = await response.json();
      setGoalValidation(validation);
    } catch (error: any) {
      console.error('Goal validation error:', error);
      if (error.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setValidationError(t('analysis.goalValidationError'));
      }
    } finally {
      setIsValidatingGoal(false);
    }
  };

  // Get the goal string to send to the API
  const getGoalString = (): string => {
    if (goalType === 'custom') {
      return customGoal;
    }
    // Map goal types to their translation keys - send the English version to the API
    const goalMap: { [key: string]: string } = {
      'muscleGroups': 'Muscular groups trained during this period',
      'strength': 'Progress on strength',
      'volume': 'Volume analysis',
      'frequency': 'Training frequency by muscle group',
      'recovery': 'Recovery patterns'
    };
    return goalMap[goalType] || '';
  };

  const handleAnalyze = () => {
    if (startDate && endDate) {
      const goalString = getGoalString();
      // If custom goal, it must be validated first
      if (goalType === 'custom' && customGoal.trim() && !goalValidation?.isValid) {
        return;
      }
      
      onAnalyze(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        goalString
      );
    }
  };

  const canAnalyze = startDate && endDate && 
    (goalType !== 'custom' || !customGoal.trim() || goalValidation?.isValid) && 
    !isLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={commonDialogStyles}>
      <DialogTitle>{t('analysis.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Date Range Selection */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('analysis.dateRangeMode')}</Typography>
            <RadioGroup
              value={dateRangeMode}
              onChange={(e) => setDateRangeMode(e.target.value as 'preset' | 'custom')}
              row
            >
              <FormControlLabel 
                value="preset" 
                control={<Radio />} 
                label={t('analysis.preset')} 
              />
              <FormControlLabel 
                value="custom" 
                control={<Radio />} 
                label={t('analysis.custom')} 
              />
            </RadioGroup>
          </Box>

          {/* Preset Range Dropdown */}
          {dateRangeMode === 'preset' && (
            <FormControl fullWidth>
              <InputLabel>{t('analysis.presetRange')}</InputLabel>
              <Select
                value={presetRange}
                label={t('analysis.presetRange')}
                onChange={(e) => setPresetRange(e.target.value)}
              >
                <MenuItem value="thisWeek">{t('analysis.thisWeek')}</MenuItem>
                <MenuItem value="last7Days">{t('analysis.last7Days')}</MenuItem>
                <MenuItem value="thisMonth">{t('analysis.thisMonth')}</MenuItem>
                <MenuItem value="last30Days">{t('analysis.last30Days')}</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Custom Date Pickers */}
          {dateRangeMode === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label={t('analysis.startDate')}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  sx={{ flex: 1 }}
                />
                <DatePicker
                  label={t('analysis.endDate')}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </LocalizationProvider>
          )}

          {/* Display selected date range */}
          {startDate && endDate && (
            <Alert severity="info">
              <Typography variant="body2">
                {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
              </Typography>
            </Alert>
          )}

          {/* Goal Type Selection */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>{t('analysis.goalType')}</Typography>
            <FormControl fullWidth>
              <InputLabel>{t('analysis.selectGoalType')}</InputLabel>
              <Select
                value={goalType}
                label={t('analysis.selectGoalType')}
                onChange={(e) => {
                  setGoalType(e.target.value);
                  setGoalValidation(null);
                  setValidationError(null);
                }}
              >
                <MenuItem value="muscleGroups">{t('analysis.goalMuscleGroups')}</MenuItem>
                <MenuItem value="strength">{t('analysis.goalStrength')}</MenuItem>
                <MenuItem value="volume">{t('analysis.goalVolume')}</MenuItem>
                <MenuItem value="frequency">{t('analysis.goalFrequency')}</MenuItem>
                <MenuItem value="recovery">{t('analysis.goalRecovery')}</MenuItem>
                <MenuItem value="custom">{t('analysis.goalCustom')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Custom Goal Text Field - Only shown when custom is selected */}
          {goalType === 'custom' && (
            <>
              <TextField
                label={t('analysis.goalCustom')}
                multiline
                rows={3}
                value={customGoal}
                onChange={(e) => {
                  setCustomGoal(e.target.value);
                  setGoalValidation(null);
                  setValidationError(null);
                }}
                placeholder={t('analysis.customGoalPlaceholder')}
                fullWidth
              />

              {customGoal.trim() && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={validateGoal}
                    disabled={isValidatingGoal}
                    startIcon={isValidatingGoal ? <CircularProgress size={20} /> : null}
                  >
                    {isValidatingGoal ? t('analysis.validatingGoal') : t('analysis.validateGoal')}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {t('analysis.validationRequired')}
                  </Typography>
                </Box>
              )}

              {customGoal.trim() && !goalValidation && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {t('analysis.goalNotValidated')}
                  </Typography>
                </Alert>
              )}

              {goalValidation && (
                <Alert 
                  severity={goalValidation.isValid ? 'success' : 'warning'}
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2">
                    {goalValidation.feedback}
                  </Typography>
                </Alert>
              )}

              {validationError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {validationError}
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('analysis.close')}</Button>
        <Button 
          onClick={handleAnalyze} 
          variant="contained" 
          disabled={!canAnalyze}
        >
          {t('analysis.analyze')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 